import React, { useEffect, useRef, useState } from 'react';

const CLOUDINARY_BASE = 'https://res.cloudinary.com/dqssg8tws/image/upload';

const makeCloudinaryLqip = (src) => {
  try {
    const m = src.match(/\/v\d+\/(.+)$/);
    if (!m) return null;
    const id = m[1];
    return `${CLOUDINARY_BASE}/w_80,q_10,e_blur:200/${id}`;
  } catch (e) {
    return null;
  }
};

const makeCloudinaryOptimized = (src) => {
  try {
    const m = src.match(/\/v\d+\/(.+)$/);
    if (!m) return src;
    const id = m[1];
    return `${CLOUDINARY_BASE}/f_auto,q_auto/${id}`;
  } catch (e) {
    return src;
  }
};

const InlinePlaceholder = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10"></svg>';

const LazyImage = ({ src, alt = '', className = '', style = {}, ...rest }) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  // Build LQIP and final optimized URLs.
  let lqip = null;
  let finalSrc = src;
  const isCloudinary = typeof src === 'string' && src.includes('res.cloudinary.com');

  if (typeof window !== 'undefined') {
    try {
      if (isCloudinary) {
        lqip = makeCloudinaryLqip(src);
        finalSrc = makeCloudinaryOptimized(src) || src;
      } else if (typeof src === 'string' && src) {
        // Local relative paths and full external URLs are rendered directly.
        // They are NOT routed through Cloudinary fetch because that endpoint
        // rejects arbitrary remote domains (HTTP 400) and the Pages origin.
        finalSrc = src;
        lqip = null;
      }
    } catch (e) {
      // fallback: use original src
      lqip = null;
      finalSrc = src;
    }
  }

  useEffect(() => {
    if (!imgRef.current) return;
    if ('IntersectionObserver' in window) {
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      }, { threshold: 0.05 });
      obs.observe(imgRef.current);
      return () => obs.disconnect();
    } else {
      // fallback: load immediately
      setInView(true);
    }
  }, []);

  useEffect(() => {
    const imgEl = imgRef.current;
    if (!imgEl || !inView) return;

    // If finalSrc is same as current src, nothing to do
    if (finalSrc && imgEl.getAttribute('data-final-src') !== finalSrc) {
      imgEl.setAttribute('data-final-src', finalSrc);
      const onLoad = () => setLoaded(true);
      const onError = () => setLoaded(true);

      // Create a new Image to preload
      const pre = new Image();
      pre.onload = onLoad;
      pre.onerror = onError;
      pre.src = finalSrc;

      // When preloaded, set the img src to final
      pre.onload = () => {
        try {
          imgEl.src = finalSrc;
        } catch (e) {}
        setLoaded(true);
      };
      pre.onerror = () => {
        setLoaded(true);
      };

      return () => {
        pre.onload = null;
        pre.onerror = null;
      };
    }
  }, [inView, finalSrc]);

  return (
    <img
      ref={imgRef}
      src={lqip || InlinePlaceholder}
      alt={alt}
      className={`${className} lazy-image ${loaded ? 'is-loaded' : 'is-loading'}`.trim()}
      style={style}
      loading="lazy"
      {...rest}
    />
  );
};

export default LazyImage;
